/**
 * "Customer tier": the commercial class of a customer, SMB or Enterprise. Only Avrio (a Super Admin) can
 * change it; a customer's own Admin sees it read-only. If an Admin could set it, a customer could promote
 * its own access, which is why avrio-backend refuses the write for every role but Super Admin.
 *
 * Tier is NOT yet used to limit which roles a customer can be given: that is a separate, later change.
 * The section says so rather than implying it already does.
 *
 * PUT /api/customers/{id}/tier { tier } (avrio-backend#216). Absent tier means not classified yet.
 */
import { memo, useEffect, useState } from 'react';
import { Alert, Button, Radio, Space, Tag, Typography } from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerConfig, CustomerTier } from '../../types';
import styles from './index.module.css';

const { Title, Text } = Typography;

export const TIER_LABELS: Record<CustomerTier, string> = {
  SMB: 'SMB',
  ENTERPRISE: 'Enterprise',
};

interface Props {
  customerId: number;
  customerName: string;
  tier?: CustomerTier;
  /** Super Admin only. Everyone else sees the tier and cannot change it. */
  canEdit: boolean;
  /** Called with the customer as the backend now has it. */
  onChanged: (updated: CustomerConfig) => void;
}

const CustomerTierSection = ({
  customerId,
  customerName,
  tier,
  canEdit,
  onChanged,
}: Props) => {
  const [choice, setChoice] = useState<CustomerTier | undefined>(tier);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A different customer, or a saved change, replaces the draft.
  useEffect(() => {
    setChoice(tier);
    setError(null);
  }, [tier, customerId]);

  const save = () => {
    if (!choice) return;
    setSaving(true);
    setError(null);
    axios
      .put<CustomerConfig>(`/api/customers/${customerId}/tier`, {
        tier: choice,
      })
      .then((res) => onChanged(res.data))
      .catch((err) => {
        const message = err?.response?.data?.message;
        setError(
          typeof message === 'string' && message.trim()
            ? `Could not save: ${message}`
            : 'Could not change the tier. Try again.',
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <section className={styles.section} aria-labelledby="customer-tier-title">
      <Title level={5} id="customer-tier-title" className={styles.title}>
        Customer tier
      </Title>

      <Space direction="vertical" size={4} className={styles.body}>
        <span>
          {tier ? (
            <Tag color={tier === 'ENTERPRISE' ? 'blue' : 'default'}>
              {TIER_LABELS[tier]}
            </Tag>
          ) : (
            <Tag>Not classified</Tag>
          )}
          <Text>
            {tier
              ? `${customerName} is on the ${TIER_LABELS[tier]} tier.`
              : `${customerName} has not been classified yet.`}
          </Text>
        </span>
      </Space>
      <Text type="secondary" className={styles.note}>
        The tier is the customer’s commercial class. Only Avrio can change it.
        It does not yet limit which roles the customer can assign.
      </Text>

      {canEdit && (
        <Space>
          <Radio.Group
            aria-label="Customer tier"
            optionType="button"
            value={choice}
            onChange={(event) => setChoice(event.target.value as CustomerTier)}
            options={(Object.keys(TIER_LABELS) as CustomerTier[]).map(
              (value) => ({ value, label: TIER_LABELS[value] }),
            )}
          />
          <Button
            type="primary"
            onClick={save}
            loading={saving}
            disabled={!choice || choice === tier}
          >
            Save tier
          </Button>
        </Space>
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          className={styles.notice}
        />
      )}
    </section>
  );
};

export default memo(CustomerTierSection);
